/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce, * as Msdyn365 from '@msdyn365-commerce/core';
import axios from 'axios';
import * as crypto from 'crypto';

// tslint:disable-next-line
const EMARSYS_API_BASE_URL = 'https://api.emarsys.net';

// tslint:disable-next-line
function getWsseHeader(user: string, secret: string) {
    // tslint:disable-next-line
    const nonce = crypto.randomBytes(16).toString('hex');
    const timestamp = new Date().toISOString();
    const digest = base64Sha1(`${nonce}${timestamp}${secret}`);

    return `UsernameToken Username="${user}", PasswordDigest="${digest}", Nonce="${nonce}", Created="${timestamp}"`;
}

// tslint:disable-next-line
function base64Sha1(str: string) {
    const hexDigest = crypto.createHash('sha1')
        .update(str)
        .digest('hex');

    return new Buffer(hexDigest).toString('base64');
}

/**
 * PostPopUpCopy Input Action
 */
export class PostPopUpCopyInput implements Msdyn365.IActionInput {
    // TODO: Construct the input needed to run the action
    constructor() {}

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `TODO`;
    public getCacheObjectType = () => 'TODO';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IPostPopUpCopyData {
    Result: string;
    Message: string;
    ErrorMessage: string;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new PostPopUpCopyInput();
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(input: PostPopUpCopyInput, ctx: Msdyn365.IActionContext): Promise<IPostPopUpCopyData> {
    const errorResponse = 'Sorry, we had an issues while subscribing, please try again later or contact support';
    // const apiSettings = Msdyn365.msdyn365Commerce.apiSettings;

    // TODO: Uncomment the below line to get the value from a service
    // const response = await Msdyn365.sendRequest<IPostPopUpCopyData[]>('/get/example/id/1', 'get');
    const postBody = ctx.requestContext.postBody;
    const contentObject = postBody?.content;

    const bodyObject = JSON.parse(contentObject!.toString());
    // tslint:disable-next-line
    if (!bodyObject?.emailAddress) {
        //ctx.telemetry.debug(`Post Emarsys Contact: no email, ${bodyObject}`);
        return {
            Result: 'Error',
            Message: errorResponse,
            ErrorMessage: `no email: ${postBody?.content}, ${JSON.stringify(postBody)}`
        };
    }

    if (ctx.requestContext.query?.failServerSide === 'true') {
        // If failServerSide query param is set, then we throw an error
        // when running server side and since this is a serverOnly action
        // we should never successfully return any data client side
        if (!msdyn365Commerce.isBrowser) {
            throw new Error('Thrown action');
        } else {
            //ctx.telemetry.debug('Post Emarsys Contact: server side fail 1');
            return {
                Message: errorResponse,
                ErrorMessage: 'Post Emarsys Commercial Contact: server side fail 1',
                Result: 'Error'
            };
        }
    } else if (ctx.requestContext.query?.returnMockData === 'true') {
        //ctx.telemetry.debug('Post Emarsys Contact: server side fail 2');
        return {
            Message: errorResponse,
            ErrorMessage: 'Post Emarsys Commercial Contact: server side fail 2',
            Result: 'Error'
        };
    }


    try {

        const usernameKeyvaultName = bodyObject?.country === 'AU' ? 'EmarsysAPIUsernameAU' : 'EmarsysAPIUsernameNZv2';
        const secretKeyvaultName = bodyObject?.country === 'AU' ? 'EmarsysAPISecretAU' : 'EmarsysAPISecretNZv2';
        const user = await msdyn365Commerce.secretManager?.getSecretValue(usernameKeyvaultName, ctx.requestContext.apiSettings.baseUrl);
        const secret = await msdyn365Commerce.secretManager?.getSecretValue(secretKeyvaultName, ctx.requestContext.apiSettings.baseUrl);
        
    
            if (!user?.value || !secret?.value) {
                const errors = {
                    usernameError: user?.error?.message,
                    secretError: user?.error?.message
                };
                //ctx.telemetry.debug(`Post Emarsys Contact: unable to get keyvault values ${JSON.stringify(errors)}`);
                return {
                    Result: 'Error',
                    Message: errorResponse,
                    ErrorMessage: `Post Emarsys Contact: unable to get keyvault values ${JSON.stringify(errors)}`,
                };
            }
    
            const headers = {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-WSSE': getWsseHeader(user?.value || '', secret?.value || '')
            };
    
            let emarsysPostBody = {
                key_id: '3',
                // tslint:disable-next-line
                3: bodyObject?.emailAddress,
                31: '1', // sets opt in true,
                // 1: bodyObject.name
                //17: bodyObject.occupation
                //Enquiry??
            };
            // if (bodyObject.firstName) {
            //     emarsysPostBody['1'] = bodyObject.firstName;
            // }
            // if (bodyObject.lastName) {
            //     emarsysPostBody['2'] = bodyObject.lastName;
            // }
    
            // Is Opt-In Checked?
            if (bodyObject?.optIn) {
                //set opt in to true in emarsys
                // emarsysPostBody[bodyObject.emarsysOptInGroupId] = '1';
                emarsysPostBody[bodyObject.emarsysGroupId] = '1';
            }
    
            // getPostContactBody(emailAddress),
            return await axios.put(`${EMARSYS_API_BASE_URL}/api/v2/contact/?create_if_not_exists=1`,
                emarsysPostBody,
                {
                    headers: headers
                })
                .then((response) => {
                    return {
                        Message: '',
                        Result: 'Success',
                        ErrorMessage: `Success message: ${JSON.stringify(response.data)}`
                    };
                })
                .catch((error) => {
                    //ctx.telemetry.debug(`Post Emarsys Contact: catch at post ${JSON.stringify(error)}`);
                    const errToReturn = error && error.response ? error.response.data : error
                    return {
                        Result: 'Error',
                        Message: errorResponse,
                        ErrorMessage: `Post Emarsys Contact: catch at post ${JSON.stringify(emarsysPostBody)} ERROR: ${JSON.stringify(errToReturn)}`,
                    };
                });
    
        } catch (e) {
            return {
                Message: errorResponse,
                Result: 'Error',
                ErrorMessage: `Post Emarsys Contact: catch ${JSON.stringify(e)}`
            };
        }

}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IPostPopUpCopyData>>action,
    // TODO: Give your data action a unique id
    id: 'PostPopUpCopy',
    input: createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
